import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, TouchableOpacity, View, Pressable, Platform, Linking } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { LOCATION_SUMMARY_COLUMNS } from '../constants/locationSummaryColumns';
import { FlatLocationSummaryRow } from '../util/hierarchyHelpers';
import { locationSummaryTableStyles as styles } from '../styles/LocationSummaryTable.styles';
import { PRIMARY_BLUE } from '../constants/colors';
import { useTheme } from '../../../theme/useTheme';
import store, { RootState } from '../../../store';
import { setSelectedLocation as setUiSelectedLocation } from '../../../store/slices/uiPreferencesSlice';
import { setSelectedLocation as setAppDataSelectedLocation } from '../../../store/slices/appDataSlice';
import { setCameFromLocationLink } from '../../../store/slices/perfectTruckAnalyticsUiSlice';
import { useToast } from '../../../components/LhToastContainer';
import DLOModal from '../../../modules/dlo/components/DLOModal';

interface LocationSummaryTableRowProps {
  row: FlatLocationSummaryRow;
  index: number;
  onToggleExpand: (rowKey: string) => void;
}

const formatNumber = (value: number): string => new Intl.NumberFormat('en-US').format(value);

const LocationSummaryTableRow: React.FC<LocationSummaryTableRowProps> = ({ row, index, onToggleExpand }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const indentation = row.level * 18;

  const getCellValue = (key: string) => {
    switch (key) {
      case 'region':
        return row.rowType === 'location' ? row.region : '';
      case 'market':
        return row.rowType === 'location' ? row.market : '';
      case 'location':
        return row.location;
      case 'created':
        return formatNumber(row.created);
      case 'target':
        return formatNumber(row.target);
      case 'approved':
        return formatNumber(row.approved);
      case 'pending':
        return formatNumber(row.pending);
      case 'declined':
        return formatNumber(row.declined);
      default:
        return '';
    }
  };

  const showExpand = row.rowType === 'location' && row.hasChildren;
  const isRegionColumn = (columnKey: string) => columnKey === 'region';
  const isLocationColumn = (columnKey: string) => columnKey === 'location';

  const rowRef = useRef<View>(null);

  return (
    <View
      ref={rowRef}
      style={[styles.row, index % 2 === 1 && styles.rowAlt, isDark && { backgroundColor: '#202020' }]}
      // @ts-ignore — web-only mouse events
      onMouseEnter={isDark ? () => {
        const el = (rowRef.current as any);
        if (el?.style) el.style.setProperty('background-color', '#2B2B2B', 'important');
      } : undefined}
      onMouseLeave={isDark ? () => {
        const el = (rowRef.current as any);
        if (el?.style) el.style.setProperty('background-color', '#202020', 'important');
      } : undefined}
    >
      {LOCATION_SUMMARY_COLUMNS.map((column) => {
        if (column.key === 'ptExecution') {
          return (
            <View key={column.key} style={[styles.cell, { minWidth: column.minWidth, flex: 1 }]}>
              <View style={styles.executionWrap}>
                <View style={[styles.executionBarTrack, isDark && { backgroundColor: '#363636' }]}>
                  <View
                    style={[
                      styles.executionBarFill,
                      { width: `${Math.max(0, Math.min(100, row.ptExecution))}%` },
                      { backgroundColor: row.ptExecution < 100 ? '#C12C01' : '#005CBC' },
                    ]}
                  />
                </View>
                <Text style={[styles.executionValue, { color: row.ptExecution < 100 ? '#C12C01' : '#005CBC' }]}>{`${row.ptExecution}%`}</Text>
              </View>
            </View>
          );
        }

        if (column.key === 'region' || column.key === 'market' || column.key === 'location') {
          return (
            <View key={column.key} style={[styles.cell, { minWidth: column.minWidth, flex: 1 }]}>
                    {isRegionColumn(column.key) ? (
                <View style={styles.rowLabelWrap}>
                  {showExpand ? (
                    <TouchableOpacity
                      style={styles.expandIconButton}
                      onPress={() => onToggleExpand(row.rowKey)}
                      testID={`toggle-${row.rowKey}`}
                    >
                      <MaterialIcons
                        name={row.expanded ? 'expand-more' : 'chevron-right'}
                        size={16}
                        color="#616161"
                      />
                    </TouchableOpacity>
                  ) : (
                    <View style={styles.expandIconButton}>
                      {row.rowType === 'week' ? <Text style={styles.weekHint}> </Text> : null}
                    </View>
                  )}
                  <Text style={styles.rowValue}>{getCellValue(column.key)}</Text>
                </View>
              ) : isLocationColumn(column.key) ? (
                <View style={[styles.rowLabelWrap, { marginLeft: indentation }]}> 
                  {row.rowType === 'location' ? (
                    <LocationLink label={getCellValue(column.key)} />
                  ) : (
                    <Text style={styles.rowLabel}>
                      {getCellValue(column.key)}
                    </Text>
                  )}
                </View>
              ) : (
                <Text style={styles.rowValue}>{getCellValue(column.key)}</Text>
              )}
            </View>
          );
        }

        return (
          <View key={column.key} style={[styles.cell, { minWidth: column.minWidth, flex: 1 }]}>
            <Text style={styles.rowValue}>{getCellValue(column.key)}</Text>
          </View>
        );
      })}
    </View>
  );
};

export default LocationSummaryTableRow;

const LocationLink: React.FC<{ label: string }> = ({ label }) => {
  const [pressed, setPressed] = useState(false);
  const [accessDeniedLabel, setAccessDeniedLabel] = useState<string | null>(null);
  const { showToast } = useToast();
  const currentLocation = useSelector(
    (state: RootState) => state.uiPreferences.selectedLocation ?? state.appData.selectedLocation,
  );

  const onPress = () => {
    // Read state synchronously from the store at the instant of the click —
    // avoids stale-closure / React-render-cycle timing issues entirely.
    const state = store.getState();
    const allLocations: any[] = (state as any).appData?.locations ?? [];

    const match = allLocations.find(
      (loc: any) =>
        (loc.name ?? loc.displayText ?? loc.locName ?? '')
          .toLowerCase()
          .trim() === label.toLowerCase().trim(),
    );

    // `allLocations` is the current user's location-with-access list. If the
    // clicked location isn't in it, the user is not authorized for it — show
    // the Access Denied popup instead of navigating, and leave all state
    // (filters, tab, sort, expanded rows) untouched.
    if (!match) {
      setAccessDeniedLabel(label);
      return;
    }

    // Enrich from the full record when available.
    const locationObj = {
      ...match,
      id: String(match.id ?? match.locationId ?? match.locId ?? label),
      displayText: match.name ?? match.displayText ?? match.locName ?? label,
    };

    // Determine whether this navigation actually changes the user's current
    // Navigator location context, so we only toast on a real change.
    const targetName = String(locationObj.displayText ?? locationObj.name ?? label).toLowerCase().trim();
    const currentName = String((currentLocation as any)?.displayText ?? (currentLocation as any)?.name ?? '')
      .toLowerCase()
      .trim();
    const isLocationChange = currentName !== '' ? currentName !== targetName : false;

    store.dispatch(setUiSelectedLocation(locationObj));
    store.dispatch(setAppDataSelectedLocation(locationObj));

    if (isLocationChange) {
      showToast({
        header: '',
        subtext: `Your location has been updated to ${locationObj.displayText ?? label}.`,
        variant: 'info',
        position: 'top',
        duration: 0,
        hideOnBackgroundClick: true,
        hideOnUserScroll: true,
        userScrollDismissDelayMs: 3000,
      });
    }

    // Flag that Perfect Truck is being entered via a Location Summary location
    // link, so the module shows the "Perfect Truck Analytics > Perfect Truck"
    // breadcrumb instead of the default "Home > Perfect Truck" breadcrumb.
    store.dispatch(setCameFromLocationLink(true));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav = (globalThis as any).appNavigate as ((screen: string, params?: any) => void) | undefined;
    if (nav) {
      nav('Perfect Truck');
      return;
    }

    // Fallback: update the URL hash (web) or open linking deep link (native)
    const route = '#Perfect%20Truck';
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.hash = route;
    } else {
      Linking.openURL(route).catch(() => {});
    }
  };

  return (
    <>
      <Pressable
        onPress={onPress}
        onPressIn={() => setPressed(true)}
        onPressOut={() => setPressed(false)}
        accessibilityRole="link"
      >
        <Text style={[styles.rowLabel, { color: PRIMARY_BLUE, textDecorationLine: pressed ? 'underline' : 'none' }]}>{label}</Text>
      </Pressable>

      <DLOModal
        visible={!!accessDeniedLabel}
        onClose={() => setAccessDeniedLabel(null)}
        title="Access Denied"
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={() => setAccessDeniedLabel(null)}
        showCloseIcon={false}
      >
        <Text style={styles.rowValue}>
          {`You do not have access to ${accessDeniedLabel}. Contact your administrator for permissions.`}
        </Text>
      </DLOModal>
    </>
  );
};

